import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

export interface MovementItem {
  id: number;
  item_id: number;
  ancienne_zone_id?: number;
  nouvelle_zone_id?: number;
  ancienne_box_id?: number;
  nouvelle_box_id?: number;
  date_mouvement: string;
  utilisateur_id?: number;
  utilisateur_nom?: string;
  raison?: string;
  source?: string;
  item?: {
    id: number;
    label: string;
    sku?: string;
  };
}

export interface MovementBox {
  id: number;
  box_id: number;
  ancienne_zone_id: number;
  nouvelle_zone_id: number;
  date_mouvement: string;
  utilisateur_id?: number;
  utilisateur_nom?: string;
  raison?: string;
  source?: string;
  box?: {
    id: number;
    nom: string;
    code: string;
  };
}

/**
 * Hook pour récupérer l'historique des mouvements d'un objet
 */
export const useItemMovements = (itemId: number, limit?: number) => {
  return useQuery<MovementItem[]>({
    queryKey: ['movements', 'items', itemId, limit],
    queryFn: async () => {
      const response = await apiClient.get(`/movements/items/${itemId}`, {
        params: { limit },
      });
      return response.data;
    },
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour récupérer les 5 derniers mouvements d'un objet
 */
export const useItemLastMovements = (itemId: number) => {
  return useQuery<MovementItem[]>({
    queryKey: ['movements', 'items', itemId, 'last'],
    queryFn: async () => {
      const response = await apiClient.get(`/movements/items/${itemId}/last`);
      return response.data;
    },
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour récupérer l'historique des mouvements d'une box
 */
export const useBoxMovements = (boxId: number, limit?: number) => {
  return useQuery<MovementBox[]>({
    queryKey: ['movements', 'boxes', boxId, limit],
    queryFn: async () => {
      const response = await apiClient.get(`/movements/boxes/${boxId}`, {
        params: { limit },
      });
      return response.data;
    },
    enabled: !!boxId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour récupérer tous les mouvements avec filtres
 */
export const useAllMovements = (filters: {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  userId?: number;
  type?: 'item' | 'box';
}) => {
  return useQuery({
    queryKey: ['movements', 'all', filters],
    queryFn: async () => {
      const response = await apiClient.get('/movements', {
        params: filters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook pour créer un mouvement d'objet
 */
export const useCreateItemMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      itemId: number;
      ancienneZoneId?: number;
      nouvelleZoneId?: number;
      ancienneBoxId?: number;
      nouvelleBoxId?: number;
      utilisateurId?: number;
      utilisateurNom?: string;
      raison?: string;
      source?: string;
    }) => {
      const response = await apiClient.post('/movements/items', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['movements', 'items', variables.itemId] });
      queryClient.invalidateQueries({ queryKey: ['movements', 'all'] });
    },
  });
};

/**
 * Hook pour créer un mouvement de box
 */
export const useCreateBoxMovement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      boxId: number;
      ancienneZoneId: number;
      nouvelleZoneId: number;
      utilisateurId?: number;
      utilisateurNom?: string;
      raison?: string;
      source?: string;
    }) => {
      const response = await apiClient.post('/movements/boxes', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ['movements', 'boxes', variables.boxId] });
      queryClient.invalidateQueries({ queryKey: ['movements', 'all'] });
    },
  });
};

/**
 * Hook pour récupérer les statistiques de mouvements
 */
export const useMovementStats = (filters: {
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['movements', 'stats', filters],
    queryFn: async () => {
      const response = await apiClient.get('/movements/stats', {
        params: filters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

